/**
 * Dev-only context for "email verified" override so we can test without actually verifying email.
 * Use useIsEmailVerifiedEffective() instead of account?.isEmailVerified where the UI should
 * respect the debug override.
 */

import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useAccount } from '~/apis';
import { hmrSafeContext } from '~/utils/hmrSafeContext';

import {
  getDebugEmailVerifiedOverride,
  setDebugEmailVerifiedOverride,
} from './debugEmailVerifiedOverride';

type SetOverride = (value: boolean) => void;

const DebugEmailVerifiedContext = hmrSafeContext<
  { override: boolean; setOverride: SetOverride } | undefined
>('__turfmaster_debug_email_verified_ctx__', undefined);

export const DebugEmailVerifiedProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [override, setOverrideState] = useState<boolean>(getDebugEmailVerifiedOverride);

  const setOverride = useCallback((value: boolean) => {
    setDebugEmailVerifiedOverride(value);
    setOverrideState(value);
  }, []);

  const value = useMemo(() => ({ override, setOverride }), [override, setOverride]);

  return (
    <DebugEmailVerifiedContext.Provider value={value}>
      {children}
    </DebugEmailVerifiedContext.Provider>
  );
};

/**
 * Returns true when the user is considered "email verified" for UI purposes:
 * either account.isEmailVerified from the server, or the dev-only override is set.
 */
export const useIsEmailVerifiedEffective = (): boolean => {
  const { data: account } = useAccount();
  const ctx = useContext(DebugEmailVerifiedContext);
  const override = ctx?.override ?? false;
  return (account?.isEmailVerified ?? false) || override;
};

/**
 * Returns the current override state and setter. Only for use in debug UI (e.g. DebugControls).
 */
export const useDebugEmailVerifiedOverride = (): {
  override: boolean;
  setOverride: SetOverride;
} => {
  const ctx = useContext(DebugEmailVerifiedContext);
  if (!ctx) {
    return {
      override: false,
      setOverride: () => {},
    };
  }
  return ctx;
};
